import React, { FC, Suspense, useContext, useEffect, useState } from 'react';
import styles from './MoviesPage.module.scss';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import ReactPaginate from 'react-paginate';
import { CustomSpinner } from '../../shared/Spinner';
import { MoviesList } from '../../components/MoviesList';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { MultiSelect } from 'react-multi-select-component';
import Select from 'react-select';

import { AuthContext } from '../../context/auth';

import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import { RangesModel, StudiosModel, TagsModel } from './types';
import { ChangeDurationModel, ChangePageModel, ChangeStudiosModel, URLQueryModel } from './types';
import { useStudiosQuery, useTagsQuery, useMoviesRangesQuery, useMoviesQuery } from './query';
import { renderFormattedTitle } from '../../utils/stringManipulation';
import { CustomLink } from 'src/components/CustomLink';
import { useHomepageAllQuery } from '../home/query';
import { CategoriesGrid } from '../../components/CategoriesGrid';

const MoviesPage: FC = () => {
  const [selected, setSelected] = useState<ChangeStudiosModel[]>([]);
  const [selectedRanges, setSelectedRanges] = useState<ChangeDurationModel[]>([]);
  const navigate = useNavigate();

  const [query, setQuery] = useState<URLQueryModel>({
    page: qs.parse(window.location.search, { ignoreQueryPrefix: true }).page || 1,
    sort: qs.parse(window.location.search, { ignoreQueryPrefix: true }).sort || '',
    studio: qs.parse(window.location.search, { ignoreQueryPrefix: true }).sort || '',
    duration: qs.parse(window.location.search, { ignoreQueryPrefix: true }).sort || '',
  });
  const authContext = useContext(AuthContext);
  const { isAuthenticated } = authContext;

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [navExpanded, setNavExpanded] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  // GET MOVIES
  const { studios, areStudiosLoading } = useStudiosQuery(isAuthenticated);
  const { ranges, areRangesLoading } = useMoviesRangesQuery(isAuthenticated);
  const { tags, areTagsLoading } = useTagsQuery(isAuthenticated);
  const { movies, meta, isMovieLoading } = useMoviesQuery(query, isAuthenticated);
  
  // GET CATEGORIES FOR NON-AUTHENTICATED USERS
  const { homePage, isLoading: isHomepageLoading } = useHomepageAllQuery(false);

  const renderMovieList = () => {
    if (isMovieLoading && !areTagsLoading) {
      return <CustomSpinner />;
    } else {
      return <MoviesList data={movies} />;
    }
  };

  const renderPagination = () => {
    if (meta) {
      return (
        <ReactPaginate
          previousLabel={<i className="fa fa-chevron-left"/>}
          nextLabel={<i className="fa fa-chevron-right"/>}
          breakLabel={'...'}
          breakClassName={styles.breakMe}
          pageCount={meta && meta.last_page}
          marginPagesDisplayed={1}
          pageRangeDisplayed={3}
          onPageChange={handlePageChange}
          forcePage={(query.page as number) - 1}
          containerClassName={'pagination'}
          activeClassName={styles.active}
          previousClassName={query.page === 1 ? `${styles.hideButton}` : `${styles.showButton}`}
          nextClassName={meta && query.page === meta.last_page ? `${styles.hideButton}` : `${styles.showButton}`}
        />
      );
    }
    if (isMovieLoading) {
      return <div style={{ height: '40px', visibility: 'hidden' }}>Loading pagination...</div>;
    }
    return null;
  };

  const closeNav = () => {
    setNavExpanded(false);
    setOpen(false);
  };

  const setNav = (expanded: boolean) => {
    setNavExpanded(expanded);
    setOpen(!open);
  };

  const options = [
    { value: 'new', label: t('moviesPage.filterNew') },
    { value: 'old', label: t('moviesPage.filterOld') },
    { value: 'title-a', label: t('moviesPage.filterA') },
    { value: 'title-d', label: t('moviesPage.filterZ') },
  ];

  const renderTags = () => {
    return (
      <ul className={styles.category}>
        <Navbar onToggle={setNav} expanded={navExpanded} className={styles.navbarWrapper} expand="md">
          <Navbar.Toggle aria-controls="category-box" className={styles.categoryButton}>
            <div>
              Kategorien
              <span id="category-head-icon-wrap" className="open">
                {open ? (
                  <i className="fa fa-caret-up" aria-hidden="true" />
                ) : (
                  <i className="fa fa-caret-down" aria-hidden="true" />
                )}
              </span>
            </div>
          </Navbar.Toggle>
          <Navbar.Collapse className={styles.categoryBox} id="category-box">
            <Nav onClick={() => closeNav()}>
              <CustomLink to={`/${language}/movies`}>
                <li className={styles.active}>Alle Filme</li>
              </CustomLink>
              {tags &&
                tags.map((tag: TagsModel['data'], index: number) => (
                  <CustomLink key={index} to={`/${language}/tag/${tag.id}`}>
                    <li>{renderFormattedTitle(tag.name)}</li>
                  </CustomLink>
                ))}
            </Nav>
          </Navbar.Collapse>
        </Navbar>
      </ul>
    );
  };

  const handleOptionChange = (data: { value: string }) => {
    setQuery({
      ...query,
      sort: data.value,
    });

    if (data.value === 'new') {
      setQuery({
        ...query,
        sort: '-license.online_start',
      });
    }
    if (data.value === 'old') {
      setQuery({
        ...query,
        sort: 'license.online_start',
      });
    }
    if (data.value === 'title-a') {
      setQuery({
        ...query,
        sort: 'content_data.title',
      });
    }
    if (data.value === 'title-d') {
      setQuery({
        ...query,
        sort: '-content_data.title',
      });
    }
  };

  const handlePageChange = (data: ChangePageModel) => {
    const selectedPage = data.selected + 1;
    setQuery({
      ...query,
      page: selectedPage,
    });
  };

  useEffect(() => {
    let formatedQuery = qs.stringify(query);
    navigate(`?${formatedQuery}`, { replace: true });
  }, [navigate, query]);

  const formatLabel = (name: string, no: number) => {
    const n = 15;
    const label = name?.length > n ? name.substr(0, n - 1) + '...' : name;
    return `${label} (${no})`;
  };

  const handleStudioChange = (values: ChangeStudiosModel[]) => {
    setSelected(values);
    if (values?.length < 0) {
    }
    const collection = values.map((v) => v.value);
    setQuery({
      ...query,
      studio: collection,
    });
  };

  const handleDurationChange = (values: ChangeDurationModel[]) => {
    setSelectedRanges(values);
    if (values?.length < 0) {
    }
    const collection = values.map((v) => v.value);
    setQuery({
      ...query,
      duration: collection,
    });
  };

  const handleResetFilters = () => {
    setQuery({
      page: 1,
    });
    setSelected([]);
    setSelectedRanges([]);
  };

  if (isAuthenticated && (areTagsLoading || areStudiosLoading || areRangesLoading)) {
    return (
      <Container className={styles.mainWrapper}>
        <CustomSpinner />
      </Container>
    );
  }
  
  if (!isAuthenticated && isHomepageLoading) {
    return (
      <Container className={styles.mainWrapper}>
        <CustomSpinner />
      </Container>
    );
  }
  
  // For non-authenticated users, show categories grid instead of movies
  if (!isAuthenticated && homePage?.data?.categories) {
    // Filter out adult categories for non-authenticated users
    const filteredCategories = homePage.data.categories.filter(category => 
      !category.name.toLowerCase().includes('erotik')
    );
    
    return (
      <Container className={styles.mainWrapper}>
        <Row>
          <Col xs={12}>
            <div className={styles.title}>
              <h2>Kategorien</h2>
            </div>
            <div className={styles.divider}></div>
          </Col>
        </Row>
        <CategoriesGrid categories={filteredCategories} />
      </Container>
    );
  }
  
  
  
  console.log('isAuthenticated', isAuthenticated);
  return (

    <Container className={styles.mainWrapper}>
      <Row>
        <Col className={styles.topNavigation} sm={{ span: 9, offset: 3 }}>
          {selected?.length > 0 || query.duration || query.duration ? (
            <React.Fragment>
              <span onClick={handleResetFilters} style={{ cursor: 'pointer', background: '#3047af', marginRight: 10 }}>
                <i style={{ padding: 5, fontSize: 20 }} className="fa fa-times" />
              </span>
              <span
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={handleResetFilters}
              >
                {t('moviesPage.resetFilters')}
              </span>
            </React.Fragment>
          ) : null}
          <div className={styles.paginationWrapper}>
            <div className={styles.pagination}>{renderPagination()}</div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <div className={styles.title}>
            <h2>{t('moviesPage.title')}</h2>
          </div>
        </Col>
        <Col className={styles.mobilePagination} xs={12}>
          {selected?.length > 0 || query.duration || query.duration ? (
            <div className={styles.resetFiltersWrapper}>
              <span
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={handleResetFilters}
              >
                {t('moviesPage.resetFilters')}
              </span>
            </div>
          ) : null}
          <div className={styles.mobilePaginationWrapper}>
            {renderPagination()}
          </div>
        </Col>
        <Col md={9}>
          <Row>
            <Col className={styles.sortWrapper} md={4}>
              <div translate="no">
                <Select
                  className="customDropdown"
                  classNamePrefix="react-select"
                  options={options}
                  defaultValue={options[0]}
                  onChange={(selectedOption) =>
                    handleOptionChange({ value: selectedOption?.value || '' })
                  }
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                />
              </div>
            </Col>

          </Row>
        </Col>
      </Row>

      <Row>
        <Col className={styles.categoryWrapper} md={3}>
          <div>
            <div className={styles.categories}>{renderTags()}</div>
          </div>
        </Col>
        <Col md={9}>
          <Suspense fallback={<CustomSpinner />}>
            <Row>
              {renderMovieList()}

              <Col sm={{ span: 9, offset: 3 }}>
                <div className={styles.paginationWrapper}>
                  <div className={styles.pagination}>{renderPagination()}</div>
                </div>
              </Col>
            </Row>
          </Suspense>
        </Col>
      </Row>
    </Container>
  );
};

export default MoviesPage;
